import { useEffect, useMemo } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';

import { ChartProvider } from '../../CustomDash/components/Chart/ChartProvider';
import { isSpecialChartType } from '../../CustomDash/components/Chart/ChartRoot/SpecialChart';
import {
  DataProvider,
  MultiMetricDataProvider,
} from '../../CustomDash/components/DataProvider/DataProvider';
import { SnowflakeDataProvider } from '../../CustomDash/components/DataProvider/SnowflakeDataProvider';
import { DataProviderQueryStatus } from '../../CustomDash/components/DataProvider/types';
import { getChartDataSourceAccessErrorMessage } from '../../CustomDash/components/DataProvider/utils/chart-data-source-access';
import { formatProviderErrorMessage } from '../../CustomDash/components/DataProvider/utils/data-provider-results-utils';
import { needsMultiMetricData } from '../../CustomDash/components/DataProvider/utils/handlebars-field-config';
import { useTabContentConfig } from '../../CustomDash/components/PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../../CustomDash/components/PageConfigProvider/types/chart';
import type { MetricValues } from '../../CustomDash/components/PageConfigProvider/types/metric';
import { useFilterContext } from '../../CustomDash/filters/contexts/FilterProvider';
import { usePlotMetric } from '../../CustomDash/hooks/usePlotMetric';
import { resolvedRoleLabelFromFilterMap } from '../global-ai-assistant-chart-preview-data-reporter';
import type { ChartDataDigest } from '../turn-outcome-digest';
import { buildChartDataDigest } from '../turn-outcome-digest';
import type { HeadlessDigestRequest } from './headless-digest-service';
import { resolveHeadlessDigest } from './headless-digest-service';

function errorDigest(
  metric: MetricValues | undefined,
  chartType: ChartType | undefined
): ChartDataDigest {
  return {
    kind: 'chart_data',
    metric: String(metric),
    chartType: String(chartType ?? 'unknown'),
    rowCount: 0,
    empty: true,
    error: true,
  };
}

/**
 * Consumes a data-provider render-prop status stream and resolves the request
 * once terminal (Error, or Success WITH a data payload — urql can emit Success
 * before the payload lands, same guard as GlobalAiChartPreviewDataReporter).
 * Renders nothing.
 */
function ProviderStatusSink({
  request,
  status,
  error,
  data,
  chartType,
  metric,
}: {
  request: HeadlessDigestRequest;
  status: DataProviderQueryStatus;
  error: unknown;
  data: unknown;
  chartType: ChartType;
  metric: MetricValues | undefined;
}) {
  // Resolved display label of the applied role filter — same source as the
  // chart-preview reporter (the AI response only carries taxonomy-relative
  // numeric role ids; this map is where the FE knows the resolved name).
  const { activeFilterMap } = useFilterContext();
  const resolvedRoleLabel = useMemo(
    () => resolvedRoleLabelFromFilterMap(activeFilterMap),
    [activeFilterMap]
  );
  useEffect(() => {
    // Opt-in status tracing for console probing (window.__gaHeadlessDebug=true):
    // distinguishes "provider never rendered" (no line at all) from "stuck
    // Fetching" (paused query) from "Success without payload" (shape gap).
    if (
      process.env.NODE_ENV === 'development' &&
      (window as { __gaHeadlessDebug?: boolean }).__gaHeadlessDebug
    ) {
      console.info(
        '[ga-headless-debug]',
        request.requestId.slice(0, 8),
        String(metric),
        status,
        data == null ? 'no-data' : 'has-data'
      );
    }
    if (status === DataProviderQueryStatus.Error) {
      if (process.env.NODE_ENV === 'development') {
        console.info(
          '[ga-headless] provider error',
          request.requestId,
          formatProviderErrorMessage(error)
        );
      }
      resolveHeadlessDigest(request.requestId, errorDigest(metric, chartType));
      return;
    }
    if (status === DataProviderQueryStatus.Success && data != null) {
      resolveHeadlessDigest(request.requestId, {
        ...buildChartDataDigest({ data, metric: String(metric), chartType }),
        ...(resolvedRoleLabel ? { resolvedRoleLabel } : {}),
      });
    }
  }, [status, error, data, chartType, metric, request, resolvedRoleLabel]);
  return null;
}

/**
 * Mirrors DataWrappedChart's provider routing (snowflake → multi-metric →
 * main) with none of its card/plot UI. Data semantics are the providers'
 * own — this component only routes and reports.
 */
function HeadlessDigestProviderRouter({
  request,
}: {
  request: HeadlessDigestRequest;
}) {
  const { item } = request;
  const chartType = item.plot?.chartType;
  const metric = usePlotMetric();
  const { primaryFilter } = useTabContentConfig();
  const { loggedInUser } = useGetLoggedInUser();

  // Shapes the digest host doesn't support resolve an error digest instead of
  // hanging to the fetch timeout: tables and "special" charts (map, text,
  // entity-summary, …) have bespoke data pipelines outside DataWrappedChart.
  const unsupported =
    chartType === undefined ||
    chartType === 'table' ||
    isSpecialChartType(chartType);
  // useGetLoggedInUser never returns null — it falls back to an EMPTY object
  // while the user-details query loads. Distinguish "not loaded yet" (wait;
  // the query's completion re-renders this leaf, and the fetch timeout
  // backstops) from "loaded without tabs" (blocked, per the access contract).
  // Without this gate an early fetch — e.g. the drawer-open warm-up racing
  // app boot — would one-shot-resolve a spurious no-access digest.
  const authLoaded = loggedInUser.id !== undefined;
  // Opt-in router tracing (window.__gaHeadlessDebug=true) — pairs with the
  // sink's status lines: a router line with NO following sink lines means the
  // provider tree rendered without ever calling its render-prop children.
  if (
    process.env.NODE_ENV === 'development' &&
    (window as { __gaHeadlessDebug?: boolean }).__gaHeadlessDebug
  ) {
    console.info(
      '[ga-headless-debug:router]',
      request.requestId.slice(0, 8),
      'metric=',
      String(metric),
      'chartType=',
      String(chartType),
      'primaryFilter=',
      String(primaryFilter?.id),
      'authLoaded=',
      authLoaded
    );
  }
  const accessErrorMessage =
    unsupported || chartType === undefined || !authLoaded
      ? undefined
      : getChartDataSourceAccessErrorMessage({
          metric,
          chartType,
          secondaryDimension: item.plot?.secondaryDimension,
          userTabs: loggedInUser.tabs ?? [],
        });
  const blocked = unsupported || accessErrorMessage !== undefined;

  useEffect(() => {
    if (blocked) {
      resolveHeadlessDigest(request.requestId, errorDigest(metric, chartType));
    }
  }, [blocked, request.requestId, metric, chartType]);

  if (blocked || chartType === undefined || !authLoaded) {
    return null;
  }

  if (item.plot?.dataSource === 'snowflake' && item.plot?.snowflakeConfig) {
    return (
      <SnowflakeDataProvider
        config={item.plot.snowflakeConfig}
        chartType={chartType}
      >
        {({ data, error, status }) => (
          <ProviderStatusSink
            request={request}
            status={status}
            error={error}
            data={data}
            chartType={chartType}
            metric={metric}
          />
        )}
      </SnowflakeDataProvider>
    );
  }

  if (needsMultiMetricData(item, chartType)) {
    return (
      <MultiMetricDataProvider
        item={item}
        chartType={chartType}
        primaryDimension={primaryFilter?.id}
      >
        {({ data, error, status }) => (
          <ProviderStatusSink
            request={request}
            status={status}
            error={error}
            data={data}
            chartType={chartType}
            metric={metric}
          />
        )}
      </MultiMetricDataProvider>
    );
  }

  return (
    <DataProvider
      chartType={chartType}
      primaryDimension={primaryFilter?.id}
      secondaryDimension={item.plot?.secondaryDimension}
      metric={metric}
    >
      {({ data, error, status }) => (
        <ProviderStatusSink
          request={request}
          status={status}
          error={error}
          data={data}
          chartType={chartType}
          metric={metric}
        />
      )}
    </DataProvider>
  );
}

/**
 * ChartProvider wrap mirrors `Chart`: `usePlotMetric` and several providers
 * read the item through `useChart()`, so the wrap is load-bearing.
 */
export function HeadlessDigestLeaf({
  request,
}: {
  request: HeadlessDigestRequest;
}) {
  return (
    <ChartProvider item={request.item}>
      <HeadlessDigestProviderRouter request={request} />
    </ChartProvider>
  );
}
